/* Offscreen Play, product page main section.
   Moved out of sections/pdp-main-op.liquid, Sep 2026 (page speed step 2), for
   the same reason as op-pdp-main.css: cached once instead of re-sent inline
   with every product view. Loaded with `defer`, so it runs after the HTML is
   parsed and never blocks it.

   NOTHING IN HERE MAY BE LIQUID. The section id, the one thing the inline
   version took from Liquid, is read from the root's data-section-id. */
(() => {
  const init = (root) => {
    if (!root || root.dataset.opInit) return;
    root.dataset.opInit = '1';

    /* gallery: slide the track to the active frame, no network request */
    const frames = root.querySelectorAll('.op-pdp__frame');
    /* The dots inside the photo (they replaced the thumbnail row, Sep 2026). */
    const thumbs = root.querySelectorAll('.op-pdp__stage [data-thumb]');
    const track = root.querySelector('[data-track]');
    const stage = root.querySelector('.op-pdp__stage');
    let current = 0;
    const setTrack = (px, animate) => {
      if (!track) return;
      track.classList.toggle('is-dragging', !animate);
      track.style.transform = 'translateX(' + px + 'px)';
    };
    const showFrame = (i, animate) => {
      i = Math.max(0, Math.min(frames.length - 1, parseInt(i, 10) || 0));
      current = i;
      const key = String(i);
      frames.forEach((f) => {
        const on = f.dataset.frame === key;
        f.classList.toggle('is-active', on);
        const v = f.querySelector('video');
        if (!v) return;
        if (!on) { v.pause(); return; }
        /* The hero clip (draft, 31 Oct 2026) is a silent preview: muted, looping. */
        if (f.hasAttribute('data-clip')) { v.muted = true; v.play().catch(() => {}); return; }
        /* Sound on by default. The frame is reached by a tap or a swipe, which
           counts as a user gesture, so browsers allow unmuted playback here.
           If one refuses (an autoplay policy, or a phone on silent that still
           reports a block), fall back to muted rather than showing a still. */
        v.muted = false;
        v.play().catch(() => { v.muted = true; v.play().catch(() => {}); });
      });
      thumbs.forEach((b) => b.setAttribute('aria-current', b.dataset.thumb === key ? 'true' : 'false'));
      /* the chosen photo now, then its neighbours, so the next swipe either
         way is already there */
      loadAround(i);
      setTrack(-i * (stage ? stage.clientWidth : 0), animate !== false);
    };

    /* ENDLESS GALLERY (owner's decision, Sep 2026). Swiping on from the last
       photo arrives at the first, and back from the first arrives at the
       last, in the SAME direction the finger is moving. It never rewinds
       across the whole strip.

       How: the photos sit side by side in one sliding track. While the finger
       drags past an end, the photo at the far end is shifted, by a transform
       on that one frame, into the empty slot just beyond, so it follows the
       finger like any neighbour (its "ghost" position). On release the track
       slides one more width into that slot. When the slide has finished, the
       ghost goes home and the track jumps, with animation off, to where that
       photo really lives. Same picture in the same place before and after
       the jump, so nothing is seen to happen. `items` is the row of frames
       (or zoom slides), so the zoom view shares this. */
    const ghost = (items, dir) => {
      const n = items.length;
      if (dir > 0) items[0].style.transform = 'translateX(' + n * 100 + '%)';
      else items[n - 1].style.transform = 'translateX(' + -n * 100 + '%)';
    };
    const unghost = (items) => {
      items[0].style.transform = '';
      items[items.length - 1].style.transform = '';
    };
    /* Run `then` once the track has finished sliding. transitionend never
       fires when transitions are off (reduced motion, or a background tab),
       so a timer longer than the slide is the net. */
    const afterSlide = (el, then) => {
      let done = false;
      const go = () => { if (done) return; done = true; el.removeEventListener('transitionend', on); then(); };
      const on = (e) => { if (e.target === el && e.propertyName === 'transform') go(); };
      el.addEventListener('transitionend', on);
      setTimeout(go, 420);
    };
    let wrapping = false;
    const wrapFrame = (dir) => {
      if (wrapping || frames.length < 2 || !stage) return;
      wrapping = true;
      const n = frames.length;
      const target = dir > 0 ? 0 : n - 1;
      ghost(frames, dir);
      loadAround(target);
      // One slot beyond the end: n widths left for forward, one right for back.
      setTrack(dir > 0 ? -n * stage.clientWidth : stage.clientWidth, true);
      afterSlide(track, () => {
        unghost(frames);
        showFrame(target, false);
        wrapping = false;
      });
    };
    /* One tap on a thumbnail shows that photo on the stage; a second tap on the
       SAME thumbnail straight after opens it whole in the lightbox (the stage
       crops to its shape, the lightbox never does). Counted from click times
       rather than the dblclick event, which phones do not send reliably; the
       thumbnails have touch-action: manipulation so the second tap is not
       taken as a double-tap zoom. Video thumbnails have no lightbox slide, so
       a second tap on one does nothing more. */
    let lastThumb = null, lastThumbAt = 0;
    thumbs.forEach((btn) => {
      btn.addEventListener('click', () => {
        const now = Date.now();
        const twice = lastThumb === btn && now - lastThumbAt < 400;
        lastThumb = btn;
        lastThumbAt = twice ? 0 : now;
        showFrame(btn.dataset.thumb);
        if (!twice) return;
        const frame = Array.from(frames).find((f) => f.dataset.frame === btn.dataset.thumb);
        if (frame && frame.dataset.zoom) lbOpen(frame);
      });
    });
    if (stage) window.addEventListener('resize', () => setTrack(-current * stage.clientWidth, false));

    /* ---------- lightbox: one slide per photo, built once ---------- */
    const lb = root.querySelector('[data-lightbox]');
    const lbTrack = lb && lb.querySelector('[data-lb-track]');
    const lbCount = lb && lb.querySelector('[data-lb-count]');
    const photos = Array.from(frames).filter((f) => f.dataset.zoom);
    const slides = [];
    if (lbTrack) {
      photos.forEach((f) => {
        const slide = document.createElement('div');
        slide.className = 'op-lb__slide';
        const img = document.createElement('img');
        img.className = 'op-lb__img';
        img.alt = (f.querySelector('img') || {}).alt || '';
        img.draggable = false;
        img.decoding = 'async';
        slide.appendChild(img);
        lbTrack.appendChild(slide);
        slides.push(img);
      });
    }

    /* WHAT LOADS, AND WHEN. Originally every gallery photo and then a
       1600px zoom render of every photo was fetched as soon as the page
       had loaded, so swiping and zooming were instant. Measured Sep 2026
       that was about 2.3 MB on every product view, most of it for photos
       never swiped to and a zoom most phones never open. Now:
         - the first photo loads with the page (eager, from the markup);
         - after load, only the NEXT photo is fetched, so the first swipe
           is still instant;
         - showing any photo fetches it and its two neighbours;
         - a 1600px zoom render is fetched only for a photo that is
           actually opened in the zoom view. Until it lands the slide shows
           the gallery photo already in cache, so opening is still instant.
       On Save-Data or a 2G connection even the look-ahead is dropped and
       photos load only when asked for. */
    const conn = navigator.connection || {};
    const lite = conn.saveData === true || /(^|-)2g$/.test(conn.effectiveType || '');
    const loadImg = (img, src) => new Promise((done) => {
      if (!img) return done();
      // is-loaded is what fades a photo in over its preview (step 5). Set on
      // error too: a photo that failed must not stay invisible over nothing.
      const finish = () => { img.classList.add('is-loaded'); done(); };
      img.addEventListener('load', finish, { once: true });
      img.addEventListener('error', finish, { once: true });
      if (src !== undefined) img.src = src;
      else if (img.dataset.src) {
        img.loading = 'eager';
        if (img.dataset.srcset) img.srcset = img.dataset.srcset;
        img.src = img.dataset.src;
        delete img.dataset.src; delete img.dataset.srcset;
      }
      else if (img.loading === 'lazy') img.loading = 'eager';
      if (img.complete && img.naturalWidth) finish();
    });
    const frameImg = (i) => (frames[i] ? frames[i].querySelector('img') : null);
    async function loadAround(i) {
      await loadImg(frameImg(i));
      if (lite) return;
      await loadImg(frameImg(i + 1));
      await loadImg(frameImg(i - 1));
    }
    const loadHi = (k) => {
      const img = slides[k];
      const hi = photos[k] && photos[k].dataset.zoom;
      if (!img || !hi || img.dataset.hi === hi || img.dataset.hiPending) return;
      img.dataset.hiPending = '1';
      const probe = new Image();
      probe.onload = () => { img.src = hi; img.dataset.hi = hi; delete img.dataset.hiPending; };
      probe.onerror = () => { delete img.dataset.hiPending; };
      probe.src = hi;
    };
    if (frames.length > 1) {
      const kick = () => setTimeout(() => {
        // Tiny previews for the photos not loaded yet, now that the page has
        // loaded and they cannot compete with the hero. About 1 KB each.
        frames.forEach((f) => {
          if (!f.dataset.lqip) return;
          f.style.backgroundImage = "url('" + f.dataset.lqip + "')";
          delete f.dataset.lqip;
        });
        // Save-Data / 2G gets the previews (they are what makes waiting
        // bearable, and cost about 1 KB each) but not the look-ahead photo.
        if (!lite) loadAround(current);
      }, 0);
      if (document.readyState === 'complete') kick();
      else window.addEventListener('load', kick, { once: true });
    }

    let lbIndex = 0;
    let s = 1, tx = 0, ty = 0;
    const lbImg = () => slides[lbIndex];
    const lbSetTrack = (px, animate) => {
      lb.classList.toggle('is-dragging', !animate);
      lbTrack.style.transform = 'translateX(' + px + 'px)';
    };
    const applyLb = (animate) => {
      const img = lbImg();
      if (!img) return;
      lb.classList.toggle('is-dragging', !animate);
      lb.classList.toggle('is-zoomed', s > 1.02);
      img.style.setProperty('--lb-s', s);
      img.style.setProperty('--lb-x', tx + 'px');
      img.style.setProperty('--lb-y', ty + 'px');
    };
    const resetZoom = (img) => {
      if (!img) return;
      img.style.setProperty('--lb-s', 1);
      img.style.setProperty('--lb-x', '0px');
      img.style.setProperty('--lb-y', '0px');
    };
    const clampPan = () => {
      const r = lbImg().getBoundingClientRect();
      const maxX = Math.max(0, (r.width - window.innerWidth) / 2);
      const maxY = Math.max(0, (r.height - window.innerHeight) / 2);
      tx = Math.max(-maxX, Math.min(maxX, tx));
      ty = Math.max(-maxY, Math.min(maxY, ty));
    };
    const lbShow = (i, animate) => {
      const prev = lbImg();
      lbIndex = Math.max(0, Math.min(photos.length - 1, i));
      if (prev && prev !== lbImg()) resetZoom(prev);
      s = 1; tx = 0; ty = 0;
      resetZoom(lbImg());
      lb.classList.remove('is-zoomed');
      /* This slide and its neighbours borrow the gallery photo, fetching it
         first if it was never shown. Only the slide actually being looked
         at then asks for its 1600px render. */
      [lbIndex - 1, lbIndex, lbIndex + 1].forEach((k) => {
        const img = slides[k];
        if (!img || img.getAttribute('src')) return;
        const g = photos[k].querySelector('img');
        loadImg(g).then(() => {
          if (!img.getAttribute('src')) img.src = (g && (g.currentSrc || g.src)) || photos[k].dataset.zoom;
        });
      });
      loadHi(lbIndex);
      if (lbCount) lbCount.textContent = photos.length > 1 ? (lbIndex + 1) + ' / ' + photos.length : '';
      lbSetTrack(-lbIndex * window.innerWidth, animate);
      showFrame(photos[lbIndex].dataset.frame);
    };
    let lbWrapping = false;
    const lbWrap = (dir) => {
      if (lbWrapping || slides.length < 2) return;
      lbWrapping = true;
      const n = slides.length;
      const items = slides.map((img) => img.parentElement);
      const target = dir > 0 ? 0 : n - 1;
      // The slide coming round needs its picture before it is seen.
      const img = slides[target];
      if (!img.getAttribute('src')) {
        const g = photos[target].querySelector('img');
        loadImg(g).then(() => { if (!img.getAttribute('src')) img.src = (g && (g.currentSrc || g.src)) || photos[target].dataset.zoom; });
      }
      ghost(items, dir);
      lbSetTrack(dir > 0 ? -n * window.innerWidth : window.innerWidth, true);
      afterSlide(lbTrack, () => {
        unghost(items);
        lbShow(target, false);
        lbWrapping = false;
      });
    };
    const lbStep = (dir) => {
      const i = lbIndex + dir;
      if (i > slides.length - 1) lbWrap(1);
      else if (i < 0) lbWrap(-1);
      else lbShow(i, true);
    };
    let lbOpenedAt = 0;
    const lbOpen = (frame) => {
      if (!lb || !photos.length) return;
      lbOpenedAt = Date.now();
      if (typeof window.clarity === 'function') window.clarity('event', 'zoom_open');
      lb.hidden = false;
      lb.classList.add('is-open');
      document.documentElement.style.overflow = 'hidden';
      lbShow(Math.max(0, photos.indexOf(frame)), false);
    };
    const lbClose = () => {
      lb.classList.remove('is-open');
      lb.hidden = true;
      lb.style.backgroundColor = '';
      s = 1; tx = 0; ty = 0; resetZoom(lbImg());
      document.documentElement.style.overflow = '';
    };
    const zoomTo = (scale, cx, cy) => {
      /* zoom about the point (cx, cy) in viewport space so the spot under
         the finger stays under the finger */
      const next = Math.max(1, Math.min(4, scale));
      const ox = cx - window.innerWidth / 2;
      const oy = cy - window.innerHeight / 2;
      tx = ox - (ox - tx) * (next / s);
      ty = oy - (oy - ty) * (next / s);
      s = next;
      if (s === 1) { tx = 0; ty = 0; }
      applyLb(true);
      requestAnimationFrame(() => { clampPan(); applyLb(true); });
    };
    if (lb && lbTrack && slides.length) {
      lb.querySelector('[data-lb-close]').addEventListener('click', lbClose);
      document.addEventListener('keydown', (e) => {
        if (lb.hidden) return;
        if (e.key === 'Escape') lbClose();
        if (e.key === 'ArrowRight') lbStep(1);
        if (e.key === 'ArrowLeft') lbStep(-1);
      });
      window.addEventListener('resize', () => { if (!lb.hidden) lbSetTrack(-lbIndex * window.innerWidth, false); });

      /* pointer handling: one finger drags (pan when zoomed, slide the
         track when not), two fingers pinch, a still tap on the backdrop
         closes, a double-tap toggles 1x / 2.5x */
      const pts = new Map();
      let pinch0 = 0, s0 = 1, drag0 = null, moved = false, lastTap = 0;
      lb.addEventListener('pointerdown', (e) => {
        if (e.target.closest('[data-lb-close]')) return;
        pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
        try { lb.setPointerCapture(e.pointerId); } catch (_) {}
        moved = false;
        if (pts.size === 1) drag0 = { x: e.clientX, y: e.clientY, tx: tx, ty: ty, t: Date.now() };
        if (pts.size === 2) {
          const ab = Array.from(pts.values());
          pinch0 = Math.hypot(ab[0].x - ab[1].x, ab[0].y - ab[1].y) || 1;
          s0 = s;
        }
      });
      lb.addEventListener('pointermove', (e) => {
        if (!pts.has(e.pointerId)) return;
        pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (pts.size === 2) {
          const ab = Array.from(pts.values());
          const d = Math.hypot(ab[0].x - ab[1].x, ab[0].y - ab[1].y);
          const cx = (ab[0].x + ab[1].x) / 2, cy = (ab[0].y + ab[1].y) / 2;
          const next = Math.max(1, Math.min(4, s0 * (d / pinch0)));
          const ox = cx - window.innerWidth / 2, oy = cy - window.innerHeight / 2;
          tx = ox - (ox - tx) * (next / s);
          ty = oy - (oy - ty) * (next / s);
          s = next;
          moved = true;
          applyLb(false);
        } else if (pts.size === 1 && drag0) {
          const dx = e.clientX - drag0.x, dy = e.clientY - drag0.y;
          if (Math.hypot(dx, dy) > 6) moved = true;
          if (s > 1.02) {
            tx = drag0.tx + dx; ty = drag0.ty + dy; applyLb(false);
          } else if (Math.abs(dx) > Math.abs(dy)) {
            const w = window.innerWidth;
            const atEnd = (lbIndex === 0 && dx > 0) || (lbIndex === slides.length - 1 && dx < 0);
            const items = slides.map((img) => img.parentElement);
            if (atEnd && slides.length > 1) ghost(items, dx < 0 ? 1 : -1);
            else if (slides.length > 1 && !lbWrapping) unghost(items);
            lbSetTrack(-lbIndex * w + (atEnd && slides.length < 2 ? dx * 0.35 : dx), false);
          } else if (dy > 0) {
            /* swipe down to close: the photo follows the finger and the
               backdrop thins out; releasing past 80px (or a flick) closes */
            ty = dy; tx = 0; applyLb(false);
            lb.style.backgroundColor = 'rgba(11,46,79,' + Math.max(0.2, 0.96 - dy / 400) + ')';
          }
        }
      });
      const up = (e) => {
        if (!pts.has(e.pointerId)) return;
        pts.delete(e.pointerId);
        if (pts.size > 0) { drag0 = null; clampPan(); applyLb(true); return; }
        const dx = e.clientX - (drag0 ? drag0.x : e.clientX);
        const dy = e.clientY - (drag0 ? drag0.y : e.clientY);
        const dt = Date.now() - (drag0 ? drag0.t : 0);
        if (moved) {
          if (s > 1.02) { clampPan(); applyLb(true); }
          else if (Math.abs(dy) > Math.abs(dx) && dy > 0) {
            const flick = dy > 60 && dt < 300;
            if (dy > 100 || flick) lbClose();
            else { ty = 0; applyLb(true); lb.style.backgroundColor = ''; }
          }
          else {
            const w = window.innerWidth;
            const flick = Math.abs(dx) > 30 && dt < 300;
            if (dx < -w / 4 || (flick && dx < 0)) lbStep(1);
            else if (dx > w / 4 || (flick && dx > 0)) lbStep(-1);
            else {
              lbSetTrack(-lbIndex * w, true);
              if (slides.length > 1) afterSlide(lbTrack, () => { if (!lbWrapping) unghost(slides.map((img) => img.parentElement)); });
            }
          }
        } else {
          const now = Date.now();
          if (now - lastTap < 320) {
            zoomTo(s > 1.02 ? 1 : 2.5, e.clientX, e.clientY);
            lastTap = 0;
          } else {
            lastTap = now;
            // Not in the first half second: a quick second tap on the photo
            // that opened it would otherwise land here and shut it again.
            if (!e.target.classList.contains('op-lb__img') && now - lbOpenedAt > 500) setTimeout(() => { if (lastTap === now) lbClose(); }, 330);
          }
        }
        drag0 = null;
      };
      lb.addEventListener('pointerup', up);
      lb.addEventListener('pointercancel', up);
      lb.addEventListener('wheel', (e) => {
        e.preventDefault();
        zoomTo(s * (e.deltaY < 0 ? 1.15 : 0.87), e.clientX, e.clientY);
      }, { passive: false });
    }

    /* Video inset on the photo (phones, "proof first" layout). It is only a
       poster: a tap hands over to the customer video rail's own play button,
       which opens that rail's full-screen player, so there is one player on
       the page. The rail builds its buttons after the page has loaded; if
       the first one is not ready yet, the tap scrolls to the rail instead.
       Taps reach it through the stage handler below, not its own listener. */
    const pip = root.querySelector('[data-op-pip]');
    const playPip = () => {
      if (typeof window.clarity === 'function') window.clarity('event', 'pip_tap');
      const ugc = root.querySelector('[data-op-ugc]');
      if (!ugc) return;
      const play = ugc.querySelector('[data-op-ugc-btn]:not([hidden])');
      if (play) { play.click(); return; }
      const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      ugc.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'center' });
    };
    /* A tap within this many px of the inset counts as a tap on it: a thumb
       aimed at a 72px button often lands just off its edge (owner, 28 Sep
       2026: "nothing happens when I tap the edge of the video"). */
    const PIP_REACH = 10;
    const nearPip = (x, y) => {
      if (!pip || !pip.offsetWidth) return false;
      const r = pip.getBoundingClientRect();
      return x > r.left - PIP_REACH && x < r.right + PIP_REACH && y > r.top - PIP_REACH && y < r.bottom + PIP_REACH;
    };

    /* Dragging the photo slides the track under the finger; releasing
       past a third of the width (or with a quick flick) settles on the next
       or previous photo, otherwise it springs back. A press that barely
       moved is a tap: on the video inset (or just off its edge) it plays the
       clip, anywhere else on a photo it opens the lightbox. Vertical
       scrolling is left to the browser via touch-action: pan-y on the track.
       Pointer events cover mouse drags on desktop too.

       FORGIVING TAPS (28 Sep 2026). Clarity showed a Samsung visitor
       tapping the photo and the inset three times with nothing happening,
       and no click logged. The tap used to be under 10px AND under 500ms, so
       a slow press or a thumb that rolled a little did nothing at all, and a
       touch that started on the inset was ignored here while the inset
       itself waited for a click the browser never sent. Now: any press that
       stays within TAP_SLOP is a tap however long it is held; a press that
       starts on the inset can still swipe the photos; and a click that
       reaches the stage without a tap having been handled (a browser that
       judged it a tap when we did not, or a keyboard) is handled the same. */
    if (stage && track) {
      const TAP_SLOP = 16;
      let x0 = 0, y0 = 0, t0 = 0, lastX = 0, lastY = 0, dragging = false, horizontal = null, activeId = null;
      let handledAt = -1e9;
      const tapAt = (target, x, y) => {
        handledAt = performance.now();
        if (target.closest('[data-op-pip]') || nearPip(x, y)) { playPip(); return; }
        // After a small sideways roll the pointer is captured and the target
        // is the stage itself; the photo on screen is the one tapped.
        const frame = target.closest('.op-pdp__frame') || frames[current];
        if (frame && frame.hasAttribute('data-clip')) { playPip(); return; }
        if (frame && frame.dataset.zoom) lbOpen(frame);
      };
      stage.addEventListener('pointerdown', (e) => {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        if (e.target.closest('video, [data-thumb]') || wrapping) return;
        activeId = e.pointerId;
        x0 = lastX = e.clientX; y0 = lastY = e.clientY; t0 = e.timeStamp;
        dragging = true; horizontal = null;
      });
      stage.addEventListener('pointermove', (e) => {
        if (!dragging || e.pointerId !== activeId) return;
        lastX = e.clientX; lastY = e.clientY;
        const dx = e.clientX - x0, dy = e.clientY - y0;
        if (horizontal === null && Math.hypot(dx, dy) > 8) horizontal = Math.abs(dx) > Math.abs(dy);
        if (!horizontal) return;
        try { stage.setPointerCapture(activeId); } catch (_) {}
        const w = stage.clientWidth;
        /* Past an end the far photo comes round to meet the finger; with a
           single photo there is nowhere to go, so that case still resists. */
        let px = -current * w + dx;
        const pastStart = current === 0 && dx > 0;
        const pastEnd = current === frames.length - 1 && dx < 0;
        if (pastStart || pastEnd) {
          if (frames.length < 2) px = -current * w + dx * 0.35;
          else {
            ghost(frames, pastEnd ? 1 : -1);
            loadImg(frameImg(pastEnd ? 0 : frames.length - 1));
          }
        } else if (frames.length > 1) unghost(frames);
        setTrack(px, false);
      });
      const finish = (e) => {
        if (!dragging || e.pointerId !== activeId) return;
        dragging = false;
        // pointercancel carries no usable position on some browsers (0, 0);
        // the last move is where the finger really was.
        const cancel = e.type === 'pointercancel';
        const x = cancel ? lastX : e.clientX, y = cancel ? lastY : e.clientY;
        const dx = x - x0, dy = y - y0;
        const dt = e.timeStamp - t0;
        if (!cancel && Math.hypot(dx, dy) < TAP_SLOP) {
          // A tap, even if the track began to follow a small roll sideways.
          if (horizontal) showFrame(current);
          tapAt(e.target, x, y);
          return;
        }
        if (horizontal) {
          const w = stage.clientWidth;
          const flick = Math.abs(dx) > 30 && dt < 300;
          const last = frames.length - 1;
          const next = dx < -w / 3 || (flick && dx < 0);
          const prev = dx > w / 3 || (flick && dx > 0);
          if (next && current === last && last > 0) wrapFrame(1);
          else if (prev && current === 0 && last > 0) wrapFrame(-1);
          else {
            showFrame(next ? current + 1 : prev ? current - 1 : current);
            // Sprang back from an end: send the ghost home once it is off screen.
            if (last > 0) afterSlide(track, () => { if (!wrapping && !dragging) unghost(frames); });
          }
        } else {
          showFrame(current);
        }
        // Whatever it was, the click a mouse sends after it is not a tap.
        handledAt = performance.now();
      };
      stage.addEventListener('pointerup', finish);
      stage.addEventListener('pointercancel', finish);
      stage.addEventListener('dragstart', (e) => e.preventDefault());
      /* The net: a click with no gesture handled just before it. Covers a
         browser that saw a tap where the pointer events did not, and Enter
         or Space on the focused inset. */
      stage.addEventListener('click', (e) => {
        if (e.target.closest('video, [data-thumb]') || wrapping) return;
        if (performance.now() - handledAt < 800) return;
        if (lb && lb.classList.contains('is-open')) return;
        tapAt(e.target, e.clientX, e.clientY);
      });
    }

    /* Review links. The href is #op-reviews-<section id>, an anchor this
       section does not itself render — the reviews live in the review app's
       block further down the template. Resolve to that block at click time
       so the link works wherever the merchant places it; if nothing matches,
       the href is left alone rather than scrolling to the wrong place.
       With no review app block on the page (removed from the product
       template for page speed, Sep 2026), fall back to this section's own
       review cards: Judge.me publishes only its latest five reviews to the
       metafield those cards read, so they are the whole list we can show. */
    const reviewsTarget = () =>
      document.getElementById('op-reviews-' + root.dataset.sectionId) ||
      // The theme's own reviews section (sections/pdp-reviews-op.liquid,
      // Sep 2026) comes before any review-app widget.
      document.querySelector('[data-op-reviews-section]') ||
      document.querySelector('.jdgm-rev-widg, .jdgm-widget, [id*="judge_me_reviews"], #shopify-product-reviews') ||
      root.querySelector('.op-pdp__reviews');
    /* JUMPING TO A SECTION (29 Sep 2026). A smooth scroll aims at the pixel
       position measured when it starts. Two things above the reviews grow
       after that moment, on the way down: the description slot (1px until
       built, then ~2,200px on a phone) and "You may also like" (fetched as it
       nears the screen). The scroll then stops short, in whatever was above:
       Clarity and the owner both saw it land halfway through the
       recommendations. So a jump now (1) builds everything held back above
       the target first (snippets/op-defer), (2) waits a frame for layout,
       then scrolls, and (3) once the scroll has come to rest checks where the
       target really is and nudges it into place, instantly, if it is off by
       more than a few pixels. The nudge covers what (1) cannot know about:
       late images, the recommendations fetch on a slow connection. */
    const scroller = document.querySelector('.page-wrapper');
    const scrollPos = () => window.scrollY + (scroller ? scroller.scrollTop : 0);
    const settled = (then) => {
      let last = scrollPos(), still = 0, ticks = 0, moved = false;
      const t0 = Date.now();
      const tick = () => {
        const p = scrollPos();
        if (p === last) still++; else { still = 0; moved = true; last = p; }
        // A smooth scroll can sit still for half a second before it starts
        // (seen on the desktop scroller, 29 Sep 2026), so stillness only
        // counts once it has moved, or after 600ms if it never does. Three
        // quiet reads (150ms) then means it has stopped; 2.5s is the net for
        // a smooth scroll that never reports rest.
        const armed = moved || Date.now() - t0 > 600;
        if ((armed && still >= 3) || ++ticks > 50) then(); else setTimeout(tick, 50);
      };
      setTimeout(tick, 50);
    };
    // One layout pass, or 60ms, whichever is first: animation frames do not
    // run in a hidden tab, and the jump must not wait on one.
    const afterLayout = (fn) => {
      let done = false;
      const go = () => { if (!done) { done = true; fn(); } };
      requestAnimationFrame(go);
      setTimeout(go, 60);
    };
    // Only the latest jump gets to correct its landing: a second tap
    // before the first has settled would otherwise be undone by it. AND
    // THE VISITOR ALWAYS WINS (owner, 29 Sep 2026: the page "snapped back"
    // to the section top once they had started scrolling on from it):
    // any touch, wheel, pointer or key cancels the correction outright,
    // and it can never fire more than 2.5s after the tap.
    let jumpToken = 0;
    const cancelJump = () => { jumpToken++; };
    ['pointerdown', 'touchstart', 'wheel', 'keydown'].forEach((n) =>
      document.addEventListener(n, cancelJump, { capture: true, passive: true })
    );
    const jumpTo = (target) => {
      const mine = ++jumpToken;
      const started = Date.now();
      if (typeof window.opDeferBuildAbove === 'function') window.opDeferBuildAbove(target);
      const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const offBy = () => {
        if (mine !== jumpToken || Date.now() - started > 2500) return false;
        const want = parseFloat(getComputedStyle(target).scrollMarginTop) || 0;
        const top = target.getBoundingClientRect().top - (scroller && scroller.scrollHeight > scroller.clientHeight ? scroller.getBoundingClientRect().top : 0);
        return Math.abs(top - want) > 4;
      };
      afterLayout(() => {
        if (mine !== jumpToken) return;
        target.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'start' });
        settled(() => {
          if (mine !== jumpToken) return;
          if (offBy()) target.scrollIntoView({ behavior: 'instant', block: 'start' });
          // Once more: the nudge itself can be swallowed by a smooth scroll
          // that was still queued (seen on the desktop scroller).
          settled(() => {
            if (mine !== jumpToken) return;
            if (offBy()) target.scrollIntoView({ behavior: 'instant', block: 'start' });
            // The jump bar re-reads which section is on screen (below).
            root.dispatchEvent(new CustomEvent('op:jumped'));
          });
        });
      });
    };

    root.querySelectorAll('[data-reviews-link]').forEach((a) => {
      a.addEventListener('click', (ev) => {
        // Every review link, the star line in the title row included, goes
        // to the full reviews section (owner, 28 Sep 2026).
        const target = reviewsTarget();
        if (!target) return;
        ev.preventDefault();
        jumpTo(target);
      });
    });

    /* Express checkout in the sticky bar: its own small form, so keep that
       form's variant and quantity equal to the main form's. The main form's
       quantity input fires "change" whenever the tier changes (see the
       section), and the variant select fires its own. The bar's inputs fire
       "change" too, in case the wallet component caches what it read. */
    const barId = root.querySelector('[data-op-bar-id]');
    const barQty = root.querySelector('[data-op-bar-qty]');
    const mainForm = root.querySelector('.op-pdp__form');
    if (mainForm && (barId || barQty)) {
      const copy = () => {
        const q = mainForm.querySelector('[name="quantity"]');
        const id = mainForm.querySelector('[name="id"]');
        if (barQty && q && barQty.value !== q.value) {
          barQty.value = q.value;
          barQty.dispatchEvent(new Event('change', { bubbles: true }));
        }
        if (barId && id && barId.value !== id.value) {
          barId.value = id.value;
          barId.dispatchEvent(new Event('change', { bubbles: true }));
        }
      };
      mainForm.addEventListener('change', copy);
      copy();
    }

    /* Jump bar (floating capsule): shown once the product title has
       scrolled up behind the header, hidden when it comes back. Not keyed to
       the photo: on phones the photo is sticky and the card slides over it,
       so it never scrolls away. A scroll listener in the capture phase
       hears both the window (phones) and .page-wrapper (desktop, where the
       wrapper is the scroller). While a button's own section is on screen
       that button is filled ("you are here"); otherwise neither is, so the
       fill never suggests a button is selected rather than pressable. Info
       scrolls to "Choose your set" (its "here" runs from that heading to the
       Add to basket button); Reviews is a data-reviews-link, handled above. */
    const topnav = root.querySelector('[data-op-topnav]');
    const titleRow = root.querySelector('.op-pdp__titlerow, .op-pdp__title');
    if (topnav && titleRow) {
      const header = document.querySelector('header-component, .header-section');
      const btns = Array.from(topnav.querySelectorAll('[data-op-topnav-for]'));
      const buyTitle = root.querySelector('.op-pdp__buy-title');
      const buyEnd = root.querySelector('.op-pdp__form .op-pdp__cta') || buyTitle;
      const spanFor = (name) => {
        if (name === 'info') {
          if (!buyTitle) return null;
          return { top: buyTitle.getBoundingClientRect().top, bottom: buyEnd.getBoundingClientRect().bottom };
        }
        const sec = document.querySelector('[data-op-reviews-section]') || document.getElementById('op-reviews-' + root.dataset.sectionId);
        return sec ? sec.getBoundingClientRect() : null;
      };
      let queued = false;
      const check = () => {
        queued = false;
        const edge = header ? header.getBoundingClientRect().bottom : 0;
        topnav.classList.toggle('is-shown', titleRow.getBoundingClientRect().bottom < edge);
        // "Here" = the section under the capsule's bottom edge, or a little below.
        const probe = topnav.getBoundingClientRect().bottom + 24;
        btns.forEach((b) => {
          const r = spanFor(b.dataset.opTopnavFor);
          if (!r) { b.classList.remove('is-current'); return; }
          b.classList.toggle('is-current', r.top <= probe && r.bottom > probe);
        });
      };
      // A frame, or 80ms: frames are throttled in a background tab.
      const queue = () => {
        if (queued) return;
        queued = true;
        let done = false;
        const go = () => { if (!done) { done = true; check(); } };
        requestAnimationFrame(go);
        setTimeout(go, 80);
      };
      document.addEventListener('scroll', queue, { passive: true, capture: true });
      // scrollend (Chrome, Firefox) and the jump's own settle: the last
      // scroll event of a jump was seen to leave the fill one read behind.
      document.addEventListener('scrollend', queue, { passive: true, capture: true });
      root.addEventListener('op:jumped', queue);
      window.addEventListener('resize', queue, { passive: true });
      check();
    }
    root.querySelectorAll('[data-op-jump]').forEach((a) => {
      a.addEventListener('click', (ev) => {
        const target = document.getElementById(a.getAttribute('href').slice(1));
        if (!target) return;
        ev.preventDefault();
        jumpTo(target);
      });
    });

    /* sticky bar appears once the inline CTA has scrolled out of view */
    const bar = root.querySelector('[data-bar]');
    const cta = root.querySelector('.op-pdp__cta');
    if (bar && cta && 'IntersectionObserver' in window) {
      new IntersectionObserver(
        ([e]) => bar.classList.toggle('is-shown', !e.isIntersecting),
        { rootMargin: '0px 0px -60px 0px' }
      ).observe(cta);
    } else if (bar) {
      bar.classList.add('is-shown');
    }

    /* ---- description videos: load near the screen, play only on it ----
       Pairs with the replace filters on p.description. A video inside a
       block hidden by CSS never intersects, so the hidden copy of a
       desktop/mobile pair never loads at all. */
    const armLazyVideos = (() => {
      const calm = lite || window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const arm = (v) => {
        if (v.dataset.opArmed) return;
        v.dataset.opArmed = '1';
        v.muted = true;
        v.querySelectorAll('source[data-src]').forEach((src) => { src.src = src.dataset.src; src.removeAttribute('data-src'); });
        // No autoplay allowed: give the visitor the controls instead of a
        // blank box they cannot start.
        if (calm) { v.controls = true; v.preload = 'metadata'; }
        // Ends the loading shimmer. With autoplay off only metadata arrives,
        // so that counts as ready too, or it would shimmer until tapped.
        const ready = () => v.classList.add('is-ready');
        v.addEventListener(calm ? 'loadedmetadata' : 'loadeddata', ready, { once: true });
        v.addEventListener('error', ready, { once: true });
        v.load();
      };
      let near = null;
      let onScreen = null;
      const watch = (v) => {
        if (v.dataset.opWatched) return;
        v.dataset.opWatched = '1';
        if (!('IntersectionObserver' in window)) { arm(v); return; }
        if (!near) {
          near = new IntersectionObserver((entries) => {
            entries.forEach((en) => { if (en.isIntersecting) { near.unobserve(en.target); arm(en.target); } });
          }, { rootMargin: '100% 0px 100% 0px' });
          onScreen = new IntersectionObserver((entries) => {
            entries.forEach((en) => {
              const vid = en.target;
              if (!vid.dataset.opArmed || calm) return;
              if (en.isIntersecting) { const p = vid.play(); if (p && p.catch) p.catch(() => {}); }
              else vid.pause();
            });
          }, { threshold: 0.2 });
        }
        near.observe(v);
        onScreen.observe(v);
        // Armed while already on screen: the on-screen observer has
        // fired by then and will not fire again, so start it here.
        v.addEventListener('loadeddata', () => {
          if (calm) return;
          const r = v.getBoundingClientRect();
          if (r.bottom > 0 && r.top < window.innerHeight && r.width > 0) { const p = v.play(); if (p && p.catch) p.catch(() => {}); }
        }, { once: true });
      };
      // Callable again for markup added after load (the description slot
      // below): each video is watched once, however often this runs.
      return (scope) => Array.from((scope || root).querySelectorAll('video[data-op-lazy-video]')).forEach(watch);
    })();
    const startVideos = () => armLazyVideos(root);
    if (document.readyState === 'complete') startVideos();
    else window.addEventListener('load', startVideos, { once: true });

    /* ---- markup built after the load event (snippets/op-defer) ----
       The description under the dropdowns and the express checkout
       button arrive as slots the scheduler fills once the first view has
       loaded. A filled slot announces itself; any videos in it then join
       the lazy video code above. */
    root.addEventListener('op:defer-built', (e) => { armLazyVideos(e.target); });

    /* ---- one review under the facts: the next quote fades in on a timer ----
       All the quotes are in the markup, stacked in one grid cell; this only
       moves the is-on class along. It waits while the tab is hidden, and
       never runs under reduced motion, where the first quote simply stays. */
    const topQuotes = Array.from(root.querySelectorAll('[data-op-topquote] [data-op-topquote-item]'));
    if (topQuotes.length > 1 && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      const holder = root.querySelector('[data-op-topquote]');
      const every = Math.max(3, parseFloat(holder.dataset.dwell) || 5) * 1000;
      let at = 0;
      setInterval(() => {
        if (document.hidden) return;
        topQuotes[at].classList.remove('is-on');
        at = (at + 1) % topQuotes.length;
        topQuotes[at].classList.add('is-on');
      }, every);
    }

    /* ---- review cards: slow advance, endless loop ----
       Rests on a card for the dwell, then glides slowly to the next.
       ANY sign of the visitor (touch, click, wheel, key, their own scroll)
       stops it, and it stays stopped until 5 seconds after the LAST such
       sign, so it never pulls a card away from someone reading or swiping.
       It also rests while the rail is off screen or the tab is hidden, and
       never runs at all under reduced motion: the cards are still there to
       swipe by hand.

       THE LOOP. The cards are cloned once onto the end (hidden from screen
       readers, so a review is never announced twice). When the view comes
       to rest a whole set along, it is moved back by exactly one set, to
       the identical-looking original. Same pixels, so nothing visibly
       happens, and the rail can run forwards forever. */
    const rail = root.querySelector('[data-op-reviews]');
    const reviewCards = rail ? Array.from(rail.querySelectorAll('[data-op-review]')) : [];
    if (rail && reviewCards.length > 1) {
      const HOLD = 5000;
      const GLIDE = 1400;
      const dwell = Math.max(2, parseFloat(rail.dataset.dwell) || 5) * 1000;
      const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      reviewCards.forEach((c) => {
        const k = c.cloneNode(true);
        k.setAttribute('aria-hidden', 'true');
        k.removeAttribute('data-op-review');
        rail.appendChild(k);
      });

      const step = () => reviewCards[1].offsetLeft - reviewCards[0].offsetLeft;
      const setWidth = () => step() * reviewCards.length;

      let gliding = false;
      let raf = 0;
      let nextAt = performance.now() + dwell;
      let onScreen = false;

      const wrap = () => {
        const w = setWidth();
        if (w > 0 && rail.scrollLeft >= w - 1) rail.scrollLeft -= w;
      };

      const stopGlide = () => {
        if (!gliding) return;
        cancelAnimationFrame(raf);
        gliding = false;
        rail.classList.remove('is-gliding');
      };

      const hold = () => {
        stopGlide();
        nextAt = performance.now() + HOLD;
      };

      const glide = () => {
        const w = step();
        if (w <= 0) return;
        const from = rail.scrollLeft;
        const to = (Math.round(from / w) + 1) * w;
        const t0 = performance.now();
        gliding = true;
        rail.classList.add('is-gliding');
        const frame = (now) => {
          if (!gliding) return;
          const k = Math.min(1, (now - t0) / GLIDE);
          const e = k < 0.5 ? 2 * k * k : 1 - Math.pow(-2 * k + 2, 2) / 2;
          rail.scrollLeft = from + (to - from) * e;
          if (k < 1) { raf = requestAnimationFrame(frame); return; }
          gliding = false;
          rail.classList.remove('is-gliding');
          wrap();
          nextAt = performance.now() + dwell;
        };
        raf = requestAnimationFrame(frame);
      };

      // The visitor. `scroll` fires for our own glide too, so it only
      // counts when we are not the ones moving it.
      ['pointerdown', 'touchstart', 'wheel', 'keydown', 'focusin'].forEach((n) =>
        rail.addEventListener(n, hold, { passive: true })
      );
      let settle = 0;
      rail.addEventListener('scroll', () => {
        if (gliding) return;
        nextAt = Math.max(nextAt, performance.now() + HOLD);
        // Wrap only once their scroll has come to rest: moving the rail
        // under a finger mid-swipe kills the swipe's momentum on iOS.
        clearTimeout(settle);
        settle = setTimeout(wrap, 140);
      }, { passive: true });

      if ('IntersectionObserver' in window) {
        new IntersectionObserver(([e]) => {
          onScreen = e.isIntersecting;
          if (onScreen) nextAt = Math.max(nextAt, performance.now() + 1500);
        }, { threshold: 0.6 }).observe(rail);
      } else {
        onScreen = true;
      }

      if (!still) {
        setInterval(() => {
          if (gliding || !onScreen || document.hidden) return;
          if (performance.now() >= nextAt) glide();
        }, 250);
      }
    }
  };
  const initAll = () => document.querySelectorAll('.op-pdp[data-section-id]').forEach(init);
  initAll();
  // The theme editor re-renders a section in place; the new root has no
  // opInit flag, so it is picked up here and the rest are skipped.
  document.addEventListener('shopify:section:load', initAll);
})();
